/**
 * Keyboard-shortcut registry. Components register bindings while mounted;
 * one document listener (components/KeyboardShortcuts) dispatches. Bindings
 * are ignored while typing in an input unless `global` is set.
 */
import { useEffect, useRef } from "react";
import { create } from "zustand";

export interface Shortcut {
  /** KeyboardEvent.key, e.g. "/", "l", "[", "?" ; modifiers via "mod+k" */
  key: string;
  description: string;
  handler: (e: KeyboardEvent) => void;
  /** fire even inside inputs */
  global?: boolean;
  /** grouping in the help overlay */
  group?: string;
}

interface RegistryState {
  shortcuts: Map<string, Shortcut>;
  register: (id: string, s: Shortcut) => void;
  unregister: (id: string) => void;
}

export const useShortcutRegistry = create<RegistryState>((set) => ({
  shortcuts: new Map(),
  register: (id, s) =>
    set((st) => {
      const next = new Map(st.shortcuts);
      next.set(id, s);
      return { shortcuts: next };
    }),
  unregister: (id) =>
    set((st) => {
      if (!st.shortcuts.has(id)) return st;
      const next = new Map(st.shortcuts);
      next.delete(id);
      return { shortcuts: next };
    }),
}));

export function isTyping(e: KeyboardEvent): boolean {
  const t = e.target as HTMLElement | null;
  if (!t) return false;
  const tag = t.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || t.isContentEditable;
}

export function comboOf(e: KeyboardEvent): string {
  const mod = e.metaKey || e.ctrlKey;
  return (mod ? "mod+" : "") + e.key;
}

let idSeq = 0;

/** Register shortcuts for the lifetime of the component. */
export function useShortcuts(shortcuts: Shortcut[], enabled = true): void {
  const register = useShortcutRegistry((s) => s.register);
  const unregister = useShortcutRegistry((s) => s.unregister);
  // Bindings are (re)registered only when a key/description changes, so the
  // dispatcher must call the *latest* handler (closures over view state go stale).
  const latest = useRef(shortcuts);
  useEffect(() => {
    latest.current = shortcuts;
  });
  useEffect(() => {
    if (!enabled) return;
    const ids = shortcuts.map((s, i) => {
      const id = `sc-${++idSeq}`;
      register(id, { ...s, handler: (e) => (latest.current[i] ?? s).handler(e) });
      return id;
    });
    return () => ids.forEach(unregister);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, register, unregister, ...shortcuts.map((s) => s.key + s.description)]);
}
